import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import FooterSection from '../components/FooterSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft } = FiIcons;

export default function Legal() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Small timeout ensures the DOM has painted before scrolling
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const sections = [
    {
      id: 'privacy',
      title: 'Privacy Policy',
      content: (
        <>
          <p className="mb-4">
            At Sans Titre, we respect your privacy and are committed to protecting any personal data you share with us. This policy outlines how we handle the information we collect when you visit our website, communicate with us, or engage our creative services.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">1. Information We Collect</h3>
          <p className="mb-4">
            We may collect personal information such as your name, email address, phone number, and company details when you initiate contact or subscribe to our communications. We also automatically collect telemetry and usage data (like IP addresses, browser types, and navigation paths) to ensure our systems remain stable and efficient.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">2. How We Use Your Information</h3>
          <p className="mb-4">
            Your data is used to provide our services, improve your experience on our platform, and communicate updates, proposals, and project deliverables. We do not sell or rent your personal information to third parties.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">3. Data Security</h3>
          <p className="mb-4">
            We implement industry-standard technical and organizational measures to safeguard your personal information against unauthorized access, alteration, disclosure, or destruction. However, no digital system is entirely impenetrable, and we cannot guarantee absolute security.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">4. Your Rights</h3>
          <p className="mb-4">
            Under applicable California laws (such as the CCPA), you have the right to request access to, deletion of, or correction of your personal data. To exercise these rights, please contact us at hello@wearesanstitre.com.
          </p>
        </>
      )
    },
    {
      id: 'terms',
      title: 'Terms of Service',
      content: (
        <>
          <p className="mb-4">
            Welcome to Sans Titre. By accessing our website or utilizing our creative and technical services, you agree to comply with and be bound by the following Terms of Service.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">1. Scope of Services</h3>
          <p className="mb-4">
            Sans Titre provides high-fidelity visual production, brand identity design, and strategic creative consulting. Specific deliverables, timelines, and compensations will be outlined in individual Statements of Work (SOW) or project agreements.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">2. Intellectual Property</h3>
          <p className="mb-4">
            Unless otherwise agreed upon in writing, all preliminary designs, concepts, and source files remain the intellectual property of Sans Titre. Upon final payment, the client is granted a license to use the final deliverables as specified in the project agreement. Our platform's underlying code, aesthetics, and methodologies remain strictly our proprietary assets.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">3. Limitation of Liability</h3>
          <p className="mb-4">
            In no event shall Sans Titre be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to our services, your use of our digital platforms, or any third-party integrations we utilize.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">4. Governing Law</h3>
          <p className="mb-4">
            These terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any legal action or proceeding related to our services shall be brought exclusively in the state or federal courts located in Sacramento, California.
          </p>
        </>
      )
    },
    {
      id: 'cookie',
      title: 'Cookie Policy',
      content: (
        <>
          <p className="mb-4">
            Our digital outpost employs cookies and similar tracking technologies to ensure optimal performance, analyze traffic, and enhance your user experience.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">1. What Are Cookies?</h3>
          <p className="mb-4">
            Cookies are small text files placed on your device when you visit our website. They help our systems recognize your browser and capture certain information to streamline your interactions.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">2. Types of Cookies We Use</h3>
          <ul className="list-disc pl-5 mb-4 text-white/70 space-y-2">
            <li><strong>Essential Cookies:</strong> Required for the fundamental operation of our website. They cannot be disabled in our systems.</li>
            <li><strong>Performance & Analytics Cookies:</strong> Allow us to count visits and traffic sources so we can measure and improve the performance of our site.</li>
            <li><strong>Functional Cookies:</strong> Enable enhanced functionality and personalization, such as remembering your preferences.</li>
          </ul>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">3. Managing Your Preferences</h3>
          <p className="mb-4">
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, some portions of our website may function sub-optimally.
          </p>
        </>
      )
    },
    {
      id: 'legal',
      title: 'Legal Information',
      content: (
        <>
          <p className="mb-4">
            Sans Titre operates as a full-production creative agency based in Sacramento, California. We are committed to transparency, ethical business practices, and clear communication with our partners and clients.
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">1. Company Details</h3>
          <p className="mb-4">
            <strong>Entity Name:</strong> Sans Titre Co.<br />
            <strong>Headquarters:</strong> Sacramento, California, USA<br />
            <strong>Contact Email:</strong> hello@wearesanstitre.com<br />
            <strong>Contact Phone:</strong> (310) 526-8412
          </p>
          <h3 className="text-xl font-semibold mb-2 mt-6 text-white/90">2. Disclaimer</h3>
          <p className="mb-4">
            The information contained on this website is for general information purposes only. While we endeavor to keep the information up to date and correct, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability with respect to the website or the information, products, services, or related graphics contained on the website for any purpose.
          </p>
        </>
      )
    }
  ];

  return (
    <div className="w-full min-h-screen bg-black text-white flex flex-col pt-24 relative overflow-hidden">
      {/* Telemetry Grid Background */}
      <div className="fixed inset-0 pointer-events-none bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
      
      <div className="max-w-4xl mx-auto px-6 w-full flex-grow mb-32 relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-16 text-xs group tracking-widest uppercase">
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Return to Outpost
        </Link>
        
        <h1 className="text-5xl md:text-7xl font-serif italic tracking-tight mb-8">Protocols & Legal</h1>
        <div className="flex items-center gap-2 text-[#00ffff] text-xs uppercase tracking-widest mb-12 border border-[#00ffff]/20 bg-[#00ffff]/5 px-3 py-1.5 w-fit rounded">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00ffff] animate-pulse"></span>
          [SYS.DOCS: VERIFIED]
        </div>
        
        <p className="text-white/60 mb-20 text-lg leading-relaxed max-w-2xl">
          Standard operational protocols, terms, and privacy guidelines for Sans Titre. 
          Effective Date: January 1, 2026.
        </p>

        <div className="space-y-24">
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-32">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-white">{section.title}</h2>
                <div className="h-px bg-white/10 flex-grow"></div>
              </div>
              <div className="text-white/70 leading-relaxed font-light">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      </div>
      <FooterSection />
    </div>
  );
}